import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getLocalizedBlockId } from 'lib/localization';
import { focus } from 'entities/block/actions';
import { selectSelected } from 'entities/selected/reducer';
import { selectActiveLocale } from 'entities/locales/reducer';
import { updateSlate } from 'entities/slate/actions';
import { selectSlate } from 'entities/slate/reducer';
import SlateEditorShape from 'entities/slate/shape';
import { update } from 'entities/step-children/actions';
import { selectStepChild } from 'entities/step-children/reducer';
import { ContentShape, StyleShape } from 'entities/step-children/shape';
import {
  BUTTON,
  EMOJI,
  IMAGE_WITH_TEXT,
  LABEL,
  COMPOSED_BLOCKS,
  getParentBlock,
  updateElement,
} from 'lib/block';
import { COLOR_MODES } from 'lib/user-preferences';
import Text from 'components/Editor/Primitives/Content/Text';
import Emoji from 'components/Editor/Primitives/Content/Emoji';

const DEFAULT_LABEL = {
  [BUTTON]: 'Next',
  [LABEL]: 'Question label',
};

const TextContainer = ({
  id,
  wrapperId,
  text,
  spans,
  label,
  style,
  readOnly,
  colorMode,
  stepId,
  content,
  selectedBlock,
  localeId,
  slateEditor,
  onClick,
  onUpdate,
  onSlateUpdate,
}) => {
  const { id: parentId, blockType } = getParentBlock(content, id) ?? {};
  const isCompose = COMPOSED_BLOCKS.includes(blockType);
  let isParentSelected = false;
  let isSelected = selectedBlock === parentId;

  if (isCompose) {
    isSelected = selectedBlock === id;

    /* WrapperId is useful when the element is a wrapper block that does not
     * have a consistent block structure. For example, consider the text inside
     * a text field. We treat the text field as a selectable structure, but it
     * does not have the 'type' block as a property.
     */
    isParentSelected = wrapperId
      ? selectedBlock === wrapperId
      : selectedBlock === parentId;
  }

  const fillSpace = blockType === IMAGE_WITH_TEXT;

  const handleUpdate = updatedValue => {
    const localizedId = getLocalizedBlockId({ blockId: id, localeId });

    const updatedContent = updateElement({
      content,
      blockId: localizedId,
      contentChunk: updatedValue,
    });

    onUpdate(stepId, updatedContent);
  };

  const handleClick = event => {
    /* If the text is part of a composed block, we must stop the click
     * event propagation. For example, if the text is inside an input field,
     * we should * avoid selecting the external block and keep the internal
     * (text) selected.
     */
    if (isCompose && selectedBlock !== id) {
      event.stopPropagation();
      onClick(id);
    }
  };

  const handleChange = ({ slateText, slateSpans }) => {
    if (slateText !== text) {
      const updatedText = {
        text: slateText,
        spans: slateSpans,
      };

      handleUpdate(updatedText);
    }
  };

  const handleBlur = ({ slateText }) => {
    const isEmptyText = slateText.trim().length === 0;

    // If the text value is empty, we revert to default text
    if (isEmptyText) {
      // If the current block is a label or acts like one (button)
      // we need to ensure a fallback text value
      const blockLabel = label
        ? LABEL
        : blockType === BUTTON
          ? BUTTON
          : undefined;

      const defaultText = DEFAULT_LABEL[blockLabel] || '';

      slateEditor.insertText(defaultText);

      const updatedText = {
        text: defaultText,
        spans: null,
      };

      handleUpdate(updatedText);
    }
  };

  return blockType === EMOJI ? (
    <Emoji id={id} text={text} style={style} onUpdate={handleUpdate} />
  ) : (
    <Text
      id={id}
      stepId={stepId}
      text={text}
      spans={spans}
      style={style}
      fillSpace={fillSpace}
      readOnly={readOnly}
      label={label}
      isSelected={isSelected}
      isComposed={isCompose}
      isParentSelected={isParentSelected}
      colorMode={colorMode}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      onSlateUpdate={onSlateUpdate}
    />
  );
};

TextContainer.propTypes = {
  id: PropTypes.string,
  wrapperId: PropTypes.string,
  text: PropTypes.string,
  spans: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      style: StyleShape,
    })
  ),
  label: PropTypes.shape({
    errorColor: PropTypes.string,
    required: PropTypes.bool,
  }),
  style: StyleShape,
  readOnly: PropTypes.bool,
  colorMode: PropTypes.oneOf(COLOR_MODES),
  stepId: PropTypes.string,
  content: ContentShape,
  selectedBlock: PropTypes.string,
  localeId: PropTypes.string,
  slateEditor: SlateEditorShape,
  onClick: PropTypes.func,
  onUpdate: PropTypes.func,
  onSlateUpdate: PropTypes.func,
};

const mapStateToProps = state => {
  const { stepChild: stepId, block: selectedBlock } =
    selectSelected(state) ?? {};
  const { content } = selectStepChild(state, stepId) ?? {};
  const slateEditor = selectSlate(state).editor ?? {};
  const { id: localeId } = selectActiveLocale(state) ?? {};

  return { stepId, content, selectedBlock, localeId, slateEditor };
};

const mapDispatchToProps = {
  onClick: focus,
  onUpdate: update,
  onSlateUpdate: updateSlate,
};

export default connect(mapStateToProps, mapDispatchToProps)(TextContainer);
